import React, { useCallback, useEffect, useState } from 'react'

import {
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  TextField,
} from '@mui/material'
import { Autocomplete } from '@mui/material'
import { captureException } from '@sentry/react'
import pLimit from 'promise-limit'
import { Button, useDataProvider, useNotify, useTranslate } from 'react-admin'
import { useImmer } from 'use-immer'

import { Stack } from '@mantine/core'
import { LXDatePeriod } from '../../components/LXDatePeriod'
import { API } from '../../helpers/Api.helper'
import { DateHelper } from '../../helpers/Date.helper'
import { Beneficiary } from '../../types/Beneficiary'
import { Statement } from '../../types/Statement'
import { YoutubeRawImport } from '../../types/YoutubeRawImport'
import genericStyles from './../../generics.module.scss'
import styles from './CreateStatementModal.module.scss'

interface StatementCreationStatus {
  status: 'loading' | 'done' | 'error'
  beneficiaryId: string
  beneficiaryName: string
  month: number
  year: number
  statement?: Statement
  error?: string
}
export interface CreateAllStatementModalProps {
  isOpen?: boolean
  onClose: (created: boolean) => void
}

const limit = pLimit(4)

export const CreateAllStatementModal: React.FC<CreateAllStatementModalProps> = ({
  isOpen = false,
  onClose,
}) => {
  const translate = useTranslate()
  const notify = useNotify()
  const [creating, setCreating] = React.useState(false)

  const [startMonth, setStartMonth] = useState(
    DateHelper.monthNumber(DateHelper.currentDate().subtract(2, 'month')),
  )
  const [startYear, setStartYear] = useState(DateHelper.currentDate().subtract(2, 'month').get('y'))
  const [endMonth, setEndMonth] = useState(
    DateHelper.monthNumber(DateHelper.currentDate().subtract(1, 'month')),
  )
  const [endYear, setEndYear] = useState(DateHelper.currentDate().subtract(1, 'month').get('y'))
  const [error, setError] = useState<string>()
  const [isAll, setIsAll] = useState(false)
  const [allBeneficiaries, setAllBeneficiaries] = useState<Beneficiary[]>([])
  const [selectedBeneficiaries, setSelectedBeneficiaries] = useState<Beneficiary[]>([])
  const dataProvider = useDataProvider()

  const [creatingStatements, updateCreatingStatements] = useImmer<{
    [beneficiaryId: string]: StatementCreationStatus
  }>({})
  const [isStatementsCreationDone, setIsStatementsCreationDone] = React.useState(false)

  const getAllBeneficiaries = useCallback(async () => {
    const response = await dataProvider.getList('beneficiaries', {
      sort: { field: 'id', order: 'ASC' },
      filter: {},
      pagination: { page: 1, perPage: 9999999 },
    })
    setAllBeneficiaries(response.data as Beneficiary[])
  }, [dataProvider])

  const getLatestTreatedReport = useCallback(async () => {
    const response = await dataProvider.getList<YoutubeRawImport>('youtubeRawImports', {
      sort: { field: 'createdAt', order: 'DESC' },
      pagination: { page: 1, perPage: 1 },
      filter: {
        status: '120_corrected',
      },
    })

    return response.data[0]
  }, [dataProvider])

  const updatePeriodWithReport = useCallback(async () => {
    const report = await getLatestTreatedReport()
    if (!report) {
      // keep the default period
      return
    }
    // narrow the period down to the report's own month, which is the one to generate
    setStartMonth(report.month)
    setStartYear(report.year)
    setEndMonth(report.month)
    setEndYear(report.year)
  }, [getLatestTreatedReport])

  useEffect(() => {
    if (!isOpen) {
      setStartMonth(DateHelper.monthNumber(DateHelper.currentDate().subtract(2, 'month')))
      setStartYear(DateHelper.currentDate().subtract(2, 'month').get('y'))
      setEndMonth(DateHelper.monthNumber(DateHelper.currentDate().subtract(1, 'month')))
      setEndYear(DateHelper.currentDate().subtract(1, 'month').get('y'))

      setCreating(false)
      setIsStatementsCreationDone(false)
      updateCreatingStatements(() => ({}))
    } else {
      getAllBeneficiaries()
      updatePeriodWithReport()
    }
  }, [getAllBeneficiaries, isOpen, updateCreatingStatements, updatePeriodWithReport])

  const closeModal = useCallback(
    (isCreated: boolean) => {
      onClose(isCreated)
      setError(undefined)
      setIsStatementsCreationDone(false)
      updateCreatingStatements(() => ({}))
    },
    [onClose, setError, updateCreatingStatements],
  )

  const getStatementsList = useCallback(() => {
    const statements: {
      beneficiaryId: Beneficiary['id']
      beneficiaryName: Beneficiary['name']
      month: number
      year: number
    }[] = []
    const beneficiaries = isAll ? allBeneficiaries : selectedBeneficiaries

    for (const b of beneficiaries) {
      // beneficiaries.forEach(b => {
      let sd = DateHelper.fromMonthYear(startMonth, startYear)
      const ed = DateHelper.fromMonthYear(endMonth, endYear)
      while (sd.isBefore(ed) || sd.isSame(ed)) {
        statements.push({
          beneficiaryId: b.id,
          beneficiaryName: b.name,
          month: DateHelper.monthNumber(sd),
          year: sd.get('y'),
        })
        sd = sd.add(1, 'month')
      }
    }
    // })

    return statements
  }, [allBeneficiaries, startMonth, endMonth, startYear, endYear, isAll, selectedBeneficiaries])

  const createAllStatements = React.useCallback(async () => {
    setCreating(true)
    setError(undefined)
    setIsStatementsCreationDone(false)
    updateCreatingStatements(() => ({}))
    try {
      const statements = getStatementsList()
      updateCreatingStatements(draft => {
        for (const st of statements) {
          draft[`${st.beneficiaryId}-${st.year}-${st.month}`] = {
            beneficiaryId: st.beneficiaryId as string,
            beneficiaryName: st.beneficiaryName,
            month: st.month,
            year: st.year,
            status: 'loading',
          }
        }
      })

      const statementsByBeneficiary = statements.reduce(
        (acc, st) => {
          acc[st.beneficiaryId] = acc[st.beneficiaryId] || []
          acc[st.beneficiaryId].push(st)
          return acc
        },
        {} as Record<
          string,
          { beneficiaryId: string; beneficiaryName: string; month: number; year: number }[]
        >,
      )
      await Promise.all(
        Object.values(statementsByBeneficiary).map(async beneficiaryStatements => {
          await limit(async () => {
            try {
              const sortedStatements = beneficiaryStatements.sort((a, b) => {
                return a.year - b.year || a.month - b.month
              })
              const alreadyDone: { [key: string]: boolean } = {}
              for (const { beneficiaryId, month, year } of sortedStatements) {
                if (
                  creatingStatements[`${beneficiaryId}-${year}-${month}`]?.status === 'done' ||
                  alreadyDone[`${beneficiaryId}-${year}-${month}`]
                ) {
                  // already created by another instance of the request
                  continue
                }
                const job = (await API.post('/api/statements/job', { beneficiaryId, month, year }))
                  .data.job
                // await the job to be done
                const fetchJobStatus = async (): Promise<any> => {
                  await new Promise(resolve => setTimeout(resolve, 1000))
                  const up = (await API.get<any>(`/api/statements/jobStatus/${job.id}`)).data?.job
                  if (up?.finishedOn) {
                    return up
                  }
                  return fetchJobStatus()
                }
                const up = await fetchJobStatus()
                if (up.returnvalue.length === 0) {
                  throw new Error('No statements created')
                }
                const statements = up.returnvalue
                for (const newStatement of statements) {
                  alreadyDone[
                    `${newStatement.beneficiaryId}-${newStatement.year}-${newStatement.month}`
                  ] = true
                }
                updateCreatingStatements(draft => {
                  for (const newStatement of statements) {
                    const key = `${newStatement.beneficiaryId}-${newStatement.year}-${newStatement.month}`
                    // the job can return statements outside the requested period,
                    // since creating one can trigger the creation of following ones
                    draft[key] = {
                      beneficiaryId: draft[key]?.beneficiaryId ?? `${newStatement.beneficiaryId}`,
                      beneficiaryName:
                        draft[key]?.beneficiaryName ??
                        beneficiaryStatements[0]?.beneficiaryName ??
                        '',
                      month: draft[key]?.month ?? newStatement.month,
                      year: draft[key]?.year ?? newStatement.year,
                      status: 'done',
                      statement: newStatement,
                    }
                  }
                })
              }
            } catch (err: any) {
              // if any error, set all non-created statements as error because statements can now trigger future statements
              console.error('Error - create statement', err)
              captureException(err)
              updateCreatingStatements(draft => {
                for (const { beneficiaryId, month, year } of beneficiaryStatements) {
                  const key = `${beneficiaryId}-${year}-${month}`
                  if (draft[key].status === 'loading') {
                    draft[key].status = 'error'
                    draft[key].error =
                      err?.response?.data?.content?.fieldError ?? err?.response?.data?.message
                  }
                }
              })
            }
          })
        }),
      )
      setIsStatementsCreationDone(true)
    } catch (e: any) {
      const message = e.response.data?.message || e.response.data
      captureException(e)
      notify(`${message}`, { type: 'error' })
      console.error('Error - create statement', e)
      setError(`${message} - ${e.response.data.content?.fieldError ?? e.response.data.content}`)
    }
  }, [getStatementsList, notify, updateCreatingStatements])

  const canCreate = React.useMemo(
    () =>
      (isAll || selectedBeneficiaries.length > 0) &&
      !DateHelper.fromMonthYear(startMonth, startYear).isAfter(
        DateHelper.fromMonthYear(endMonth, endYear),
      ),
    [isAll, selectedBeneficiaries, startMonth, startYear, endMonth, endYear],
  )

  const resultsDisplay = React.useMemo(() => {
    return Object.values(creatingStatements).sort((a, b) => {
      if (a.status === b.status) {
        return a.beneficiaryName.localeCompare(b.beneficiaryName)
      }
      if (a.status === 'error') {
        return -1
      }
      if (b.status === 'error') {
        return 1
      }
      if (a.status === 'loading') {
        return -1
      }

      return 1
    })
  }, [creatingStatements])

  return (
    <Dialog
      aria-labelledby="youtube-import-modal"
      open={isOpen}
      fullWidth
      maxWidth="md"
      onClose={() => closeModal(false)}
    >
      <DialogTitle id="youtube-import-modal">{translate('pages.statements.create')}</DialogTitle>
      <DialogContent dividers>
        <Grid container direction="row" spacing={6}>
          <Grid container item direction="column" lg={6}>
            <Grid item>
              <InputLabel id="beneficiaries-autocomplete" style={{ fontSize: '80%' }}>
                {translate('pages.beneficiaries._title')}
              </InputLabel>
              <Autocomplete
                id="beneficiaries-autocomplete"
                options={allBeneficiaries}
                getOptionLabel={option => option.name}
                renderInput={params => <TextField {...params} variant="standard" />}
                onChange={(_, value) => setSelectedBeneficiaries(value)}
                disableCloseOnSelect
                multiple
                disabled={isAll || creating}
                classes={{
                  tag: genericStyles.tagRemove,
                }}
              />
            </Grid>
            <Grid item>
              <FormGroup>
                <FormControlLabel
                  disabled={creating}
                  control={
                    <Checkbox
                      checked={isAll}
                      size="small"
                      onChange={e => setIsAll(e.target.checked)}
                    />
                  }
                  label={translate('pages.beneficiaries.allBeneficiaries')}
                />
              </FormGroup>
            </Grid>
          </Grid>
          <Grid container item direction="row" lg={6}>
            <Grid item lg={8}>
              <Stack>
                <LXDatePeriod
                  month={startMonth?.toString()}
                  year={startYear?.toString()}
                  monthLabel={translate('_generics.startDate')}
                  yearLabel=" "
                  minYear={DateHelper.january2021Date().get('y')}
                  onUpdate={(month: string | undefined, year: string | undefined): void => {
                    if (month) {
                      setStartMonth(parseInt(month))
                    }
                    if (year) {
                      setStartYear(parseInt(year))
                    }
                  }}
                  disabled={creating}
                />
                <LXDatePeriod
                  month={endMonth?.toString()}
                  year={endYear?.toString()}
                  monthLabel={translate('_generics.endDate')}
                  yearLabel=" "
                  minYear={DateHelper.january2021Date().get('y')}
                  onUpdate={(month: string | undefined, year: string | undefined): void => {
                    if (month) {
                      setEndMonth(parseInt(month))
                    }
                    if (year) {
                      setEndYear(parseInt(year))
                    }
                  }}
                  disabled={creating}
                />
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        {creating && (
          <div className={styles.status}>
            {!isStatementsCreationDone && <CircularProgress size="1rem" />}
            <div className="text-left">
              {resultsDisplay.map(
                ({
                  beneficiaryId,
                  beneficiaryName,
                  month,
                  year,
                  status,
                  error: berror,
                  statement,
                }) => (
                  <div key={`${beneficiaryId}-${year}-${month}`}>
                    <span
                      style={{
                        color: status === 'error' ? 'red' : status === 'done' ? 'green' : undefined,
                      }}
                    >
                      {status === 'done' ? ' ✅ ' : status === 'error' ? ' ❌ ' : ' 🔄 '}{' '}
                      {beneficiaryName} {month}/{year} {status === 'error' ? `- ${berror}` : ''}
                    </span>
                    {status === 'done' && (
                      <span>
                        {' '}
                        {Math.round(Number.parseFloat(statement?.netRevenue ?? '0'))}
                        €
                      </span>
                    )}
                  </div>
                ),
              )}
            </div>
          </div>
        )}
        {error && (
          <p className={genericStyles.error}>
            <i>{translate('_generics.errorOccured')}</i>:&nbsp;
            <strong>{error}</strong>
          </p>
        )}
      </DialogContent>
      <DialogActions>
        {!creating && (
          <>
            <Button
              onClick={() => closeModal(false)}
              label={translate('_generics.cancel')}
              disabled={creating && !error}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                createAllStatements()
              }}
              label={translate('_generics.create')}
              disabled={creating || !canCreate}
            />
          </>
        )}
        {isStatementsCreationDone && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              closeModal(true)
            }}
            label={translate('_generics.ok')}
          />
        )}
      </DialogActions>
    </Dialog>
  )
}
